import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import { toast } from '../../toasts';
import BillService from '../../services/bill.service';
import { getError } from '../../utils';
import { useLabels } from '../../hooks/useLabels';
import { Button, FormField, controlClass } from '../../components/EntityList';

/**
 * Replace an estimated (placeholder) bill amount with the real invoiced one —
 * SHOW_BILL_ESTIMATES. The invoice PDF, if any, can be attached from the
 * bill's Documents tab; invoices arriving through the integration layer set
 * the amount and attach the PDF on their own.
 */
export default function BillActualAmountModal({ bill, onClose, onSaved }) {
	const { t } = useLabels();
	const [amount, setAmount] = useState(bill.amount ?? '');
	const [saving, setSaving] = useState(false);

	const handleSave = async (event) => {
		event.preventDefault();
		if (amount === '' || Number(amount) < 0) {
			toast.error(t('billEstimates.invalidAmount'));
			return;
		}
		setSaving(true);
		try {
			await BillService.setActualAmount(bill._id, { amount: Number(amount) });
			toast.success(t('billEstimates.saved'));
			onSaved();
			onClose();
		} catch (error) {
			toast.error(getError(error));
		}
		setSaving(false);
	};

	return (
		<Modal show onHide={onClose} centered>
			<Form onSubmit={handleSave}>
				<Modal.Header closeButton>
					<Modal.Title className='tw:text-[15px]'>{t('billEstimates.setActual')}</Modal.Title>
				</Modal.Header>
				<Modal.Body className='tw:space-y-3'>
					{bill.amountStatus === 'estimated' && (
						<p className='tw:m-0 tw:text-[12.5px] tw:text-ink-2'>
							{t('billEstimates.estimatedWas')}: <span className='tw:font-mono'>{bill.estimatedAmount ?? bill.amount}</span>
						</p>
					)}
					<FormField id='bill-actual-amount' label={t('billEstimates.actualAmount')} required>
						<Form.Control
							id='bill-actual-amount'
							type='number'
							step='any'
							min={0}
							autoFocus
							className={controlClass}
							value={amount}
							onChange={(e) => setAmount(e.target.value)}
						/>
					</FormField>
				</Modal.Body>
				<Modal.Footer>
					<Button onClick={onClose} disabled={saving}>{t('action.cancel')}</Button>
					<Button variant='primary' type='submit' disabled={saving}>{t('action.save')}</Button>
				</Modal.Footer>
			</Form>
		</Modal>
	);
}

BillActualAmountModal.propTypes = {
	bill: PropTypes.object.isRequired,
	onClose: PropTypes.func.isRequired,
	onSaved: PropTypes.func.isRequired,
};
